/* ppb-label-engine.js — K&K custom label renderer + print-file generator.
 *
 * Replaces Customily for the Custom Pickle Pack builder. One renderer draws
 * both the live preview and the 600 DPI print file, so what the customer
 * sees is exactly what fulfillment prints.
 *
 * Print spec (reverse-engineered from real Customily production PDFs, see
 * kk-jar-ladder/docs/customily-print-spec.md):
 *   - label 2.58in x 3.00in  (PDF page 185.76 x 216 pt)
 *   - flattened raster at 600 DPI = 1550 x 1800 px
 *   - design space: 775 x 900 units (1 unit = 2 print px)
 *   - headline/message font: FF DIN Round Pro Bold (shipped as theme asset)
 *   - template art + per-flavor footers: pixel assets lifted from the
 *     original Customily templates (drawn full-bleed)
 *
 * Exposed as window.PPBLabel = { TEMPLATES, init, render, buildPrintPdf, ... }.
 * Asset URLs are injected by the section via window.PPB_ASSETS.
 */
(function () {
  'use strict';

  var W = 775, H = 900;            /* design units */
  var PRINT_W = 1550, PRINT_H = 1800;
  var PAGE_W = 185.76, PAGE_H = 216; /* PDF points (2.58in x 3.00in) */

  var A = window.PPB_ASSETS || {}; /* name -> theme asset URL */

  /* ---------- template registry ----------
   * zones are in design units. photo zone shrinks to msgClear.bottom when a
   * message is present so text never overlaps the photo (an upgrade over
   * Customily, which lets them collide). */
  var TEMPLATES = [
    {
      key: 'fully-custom', label: 'Fully Custom',
      art: 'art-fullycustom-2x', footer: 'plain',
      bgDefault: '#AB0F1C',
      fields: { name: { max: 45, zone: { cx: W / 2, cy: 158, maxW: 700, maxSize: 106 } },
                message: { max: 45, zone: { cx: W / 2, cy: 754, maxW: 660, maxSize: 44 } } },
      /* yNoName: with no name typed, the photo grows up into the empty headline space
         (2026-10-19, order #60684: a full logo reads better big than under a repeated name) */
      photo: { x: 38, y: 232, w: 700, h: 540, whenMsg: 500, yNoName: 150 },
      photoRequired: false
    },
    {
      key: 'birthday', label: 'Happy Birthday',
      art: 'art-birthday-2x', footer: 'plain',
      bgDefault: '#588017',
      fields: { name: { max: 45, zone: { cx: 420, cy: 42, maxW: 430, maxSize: 52 } },
                message: { max: 45, zone: { cx: W / 2, cy: 735, maxW: 700, maxSize: 34 } } },
      photo: { x: 38, y: 270, w: 700, h: 500, whenMsg: 440 },
      photoRequired: true
    },
    {
      /* 2026-09-30: K&K's anniversary campaign (newsletter + socials). Art lifted from their
         flat file: rose background keyed to transparency, generic footer removed (the engine
         draws the per-flavor footer). Photo sits left of the balloon bunch; the optional
         message line stays clear of the balloons. */
      key: 'anniversary', label: 'Happy Anniversary',
      art: 'art-anniversary-2x', footer: 'plain',
      bgDefault: '#AC515B',
      fields: { message: { max: 45, zone: { cx: 300, cy: 738, maxW: 500, maxSize: 34 } } },
      photo: { x: 30, y: 262, w: 560, h: 490, whenMsg: 450 },
      photoRequired: true
    },
    /* 2026-10-23: holiday cards (Thanksgiving / Christmas / Hanukkah). Art = K+K logo, Gochi Hand
       headline and white line doodles on a transparent 1550x1800 layer; photo spans the label
       width under the headline, optional message line sits between photo and flavor footer. */
    {
      key: 'thanksgiving', label: 'Happy Thanksgiving',
      art: 'art-thanksgiving-2x', footer: 'plain',
      bgDefault: '#B4532A',
      fields: { message: { max: 45, zone: { cx: W / 2, cy: 735, maxW: 700, maxSize: 34 } } },
      photo: { x: 38, y: 262, w: 700, h: 510, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'christmas', label: 'Merry Christmas',
      art: 'art-christmas-2x', footer: 'plain',
      bgDefault: '#1E5B3A',
      fields: { message: { max: 45, zone: { cx: W / 2, cy: 735, maxW: 700, maxSize: 34 } } },
      photo: { x: 38, y: 262, w: 700, h: 510, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'hanukkah', label: 'Happy Hanukkah',
      art: 'art-hanukkah-2x', footer: 'plain',
      bgDefault: '#1F4E8C',
      fields: { message: { max: 45, zone: { cx: W / 2, cy: 735, maxW: 700, maxSize: 34 } } },
      photo: { x: 38, y: 262, w: 700, h: 510, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'cyom', label: 'Your Message + Hearts',
      art: 'art-cyom', footer: 'plain',
      bgDefault: '#AB0F1C', swatchDefault: 'Red',
      fields: { message: { max: 73, zone: { cx: W / 2, cy: 96, maxW: 690, maxSize: 84, lines: 2 } } },
      photo: { x: 60, y: 215, w: 655, h: 535, whenMsg: 535 },
      photoRequired: true
    },
    {
      key: 'tickle', label: 'You Tickle My Pickle',
      art: 'art-tickle', footer: 'plain',
      bgDefault: '#AB0F1C', flavors4: true,
      fields: { name: { max: 20, key: 'To', uiLabel: 'To (their name)', uiPh: 'e.g. Jessi',
                  zone: { align: 'left', x: 42, cy: 182, maxW: 250, maxSize: 56 } },
                message: { max: 20, key: 'From', uiLabel: 'From (your name, next to the heart)', uiPh: 'e.g. Tom',
                  zone: { align: 'left', x: 178, cx: 440, cy: 692, maxW: 540, maxSize: 56, lines: 1 } } },
      photo: { x: 300, y: 170, w: 445, h: 440, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'forever', label: 'Forever My Snack',
      art: 'art-forever', footer: 'plain',
      bgDefault: '#AB0F1C', flavors4: true,
      fields: { name: { max: 20, key: 'To', uiLabel: 'To (their name)', uiPh: 'e.g. Jessi',
                  zone: { align: 'left', x: 42, cy: 190, maxW: 250, maxSize: 56 } },
                message: { max: 20, key: 'From', uiLabel: 'From (your name, next to the heart)', uiPh: 'e.g. Tom',
                  zone: { align: 'left', x: 178, cx: 440, cy: 692, maxW: 540, maxSize: 56, lines: 1 } } },
      photo: { x: 300, y: 170, w: 445, h: 440, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'bemine', label: 'Be Mine',
      art: 'art-bemine', footer: 'plain',
      bgDefault: '#AB0F1C', flavors4: true,
      fields: { name: { max: 20, key: 'To', uiLabel: 'To (their name)', uiPh: 'e.g. Jessi',
                  zone: { align: 'left', x: 42, cy: 294, maxW: 250, maxSize: 56 } },
                message: { max: 20, key: 'From', uiLabel: 'From (your name, next to the heart)', uiPh: 'e.g. Tom',
                  zone: { align: 'left', x: 178, cx: 440, cy: 692, maxW: 540, maxSize: 56, lines: 1 } } },
      photo: { x: 300, y: 170, w: 445, h: 440, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'happyval', label: "Happy Valentine's Day",
      art: 'art-happyval', footer: 'plain',
      bgDefault: '#AB0F1C', flavors4: true,
      fields: { name: { max: 20, key: 'To', uiLabel: 'To (their name)', uiPh: 'e.g. Jessi',
                  zone: { align: 'left', x: 42, cy: 352, maxW: 250, maxSize: 56 } },
                message: { max: 20, key: 'From', uiLabel: 'From (your name, next to the heart)', uiPh: 'e.g. Tom',
                  zone: { align: 'left', x: 178, cx: 440, cy: 692, maxW: 540, maxSize: 56, lines: 1 } } },
      photo: { x: 300, y: 170, w: 445, h: 440, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'bachelorette', label: 'Bachelorette',
      art: 'art-bachelorette', footer: 'plain',
      bgDefault: '#B69AF2',
      fields: { message: { max: 45, zone: { cx: W / 2, cy: 738, maxW: 660, maxSize: 36 } } },
      photo: { x: 38, y: 250, w: 700, h: 480, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'bridesmaid', label: 'Will You Be My Bridesmaid?',
      art: 'art-bridesmaid', footer: 'plain',
      bgDefault: '#B69AF2',
      fields: { message: { max: 45, zone: { cx: W / 2, cy: 738, maxW: 660, maxSize: 36 } } },
      photo: { x: 38, y: 250, w: 700, h: 480, whenMsg: 440 },
      photoRequired: true
    },
    {
      key: 'grad', label: 'Congrats Grad!',
      art: 'art-grad', footer: 'plain',
      bgDefault: '#2B2B2B',
      fields: { message: { max: 45, zone: { cx: W / 2, cy: 738, maxW: 660, maxSize: 36 } } },
      photo: { x: 38, y: 250, w: 700, h: 480, whenMsg: 440 },
      photoRequired: true
    }
  ];

  /* flavor key from a builder card title */
  function flavorKey(title) {
    var t = String(title).toLowerCase();
    if (t.indexOf('tomato') > -1) return t.indexOf('whole') > -1 ? 'wholetomatoes' : 'tomatoslices';
    if (t.indexOf('honey mustard') > -1) return 'honeymustard';
    if (t.indexOf('sweet heat') > -1) return 'sweetheat';
    if (t.indexOf('spicy') > -1) return 'spicygarlic';
    return 'classicdill';
  }

  var PLAIN_FOOT = {
    classicdill: 'foot-classicdill-2x', spicygarlic: 'foot-spicygarlic-2x',
    honeymustard: 'foot-honeymustard', sweetheat: 'foot-sweetheat',
    tomatoslices: 'foot-tomatoslices', wholetomatoes: 'foot-wholetomatoes'
  };
  /* ingredient footers exist for the 4 core flavors; tomatoes fall back to
     the plain strip so every flavor still prints a correct footer */
  var ING_FOOT = {
    classicdill: 'footing-classicdill', spicygarlic: 'footing-spicygarlic',
    honeymustard: 'footing-honeymustard', sweetheat: 'footing-sweetheat',
    tomatoslices: 'foot-tomatoslices', wholetomatoes: 'foot-wholetomatoes'
  };

  function footerAsset(tpl, fkey) {
    if (tpl.footer === 'none') return null;
    var map = tpl.footer === 'ingredients' ? ING_FOOT : PLAIN_FOOT;
    return map[fkey] || PLAIN_FOOT.classicdill;
  }

  /* ---------- asset loading ---------- */
  var imgCache = {};
  function loadImg(name) {
    if (!name) return Promise.resolve(null);
    if (!imgCache[name]) {
      imgCache[name] = new Promise(function (resolve, reject) {
        var url = A[name];
        if (!url) return reject(new Error('missing asset ' + name));
        var im = new Image();
        im.onload = function () { resolve(im); };
        im.onerror = function () { reject(new Error('asset failed ' + name)); };
        im.src = url;
      });
    }
    return imgCache[name];
  }

  var fontPromise = null;
  function loadFont() {
    if (!fontPromise) {
      fontPromise = A.font
        ? new FontFace('PPBDinRound', 'url(' + A.font + ')', { weight: '700' }).load()
            .then(function (ff) { document.fonts.add(ff); return true; })
            .catch(function () { return false; })
        : Promise.resolve(false);
    }
    return fontPromise;
  }

  function fontStack(px) {
    return '700 ' + px + "px PPBDinRound, 'Arial Rounded MT Bold', Arial, sans-serif";
  }

  /* ---------- text helpers ---------- */
  function fitLine(ctx, text, maxW, maxSize) {
    var size = maxSize;
    ctx.font = fontStack(size);
    var w = ctx.measureText(text).width;
    if (w > maxW) size = Math.max(14, Math.floor(size * maxW / w));
    return size;
  }
  /* wrap into up to `maxLines`, shrinking until it fits (and, when maxH is
     given, until the whole block is no taller than maxH) */
  function fitWrapped(ctx, text, maxW, maxSize, maxLines, maxH) {
    maxLines = maxLines || 2;
    var size = maxSize;
    while (size > 14) {
      ctx.font = fontStack(size);
      var words = text.split(/\s+/), lines = [], cur = '';
      for (var i = 0; i < words.length; i++) {
        var t = cur ? cur + ' ' + words[i] : words[i];
        if (ctx.measureText(t).width <= maxW || !cur) cur = t;
        else { lines.push(cur); cur = words[i]; }
      }
      if (cur) lines.push(cur);
      var ok = lines.length <= maxLines;
      for (var j = 0; j < lines.length; j++) if (ctx.measureText(lines[j]).width > maxW) ok = false;
      if (maxH && blockHeight(size, lines.length) > maxH) ok = false;
      if (ok) return { size: size, lines: lines };
      size -= 3;
    }
    return { size: 14, lines: [text] };
  }

  /* text block height: line gaps + half a line of ink above and below */
  var LH = 1.18, INK = 0.55;
  function blockHeight(size, n) { return (n - 1) * size * LH + 2 * INK * size; }

  /* Footers (the per-flavor "<FLAVOR> PICKLES | KEEP REFRIGERATED" strip) start at
     y ~790 design units across all flavors. Bottom text must end above this. */
  var FOOT_TOP = 784;

  /* photo zone actually used for this state: shrinks for a message, grows into
     the headline space when the template allows it and no name is typed */
  function photoZone(tpl, state, hasMsg) {
    var z = tpl.photo;
    var top = z.y, bottom = z.y + (hasMsg ? (z.whenMsg || z.h) : z.h);
    if (z.yNoName != null && tpl.fields.name && !(state.name && state.name.trim())) top = z.yNoName;
    return { x: z.x, y: top, w: z.w, h: bottom - top };
  }

  /* WCAG-ish relative luminance for the contrast advisory */
  function luminance(hex) {
    var m = /^#?([0-9a-f]{6})/i.exec(hex);
    if (!m) return 0;
    var n = parseInt(m[1], 16);
    var f = function (c) { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
    return 0.2126 * f((n >> 16) & 255) + 0.7152 * f((n >> 8) & 255) + 0.0722 * f(n & 255);
  }
  function contrastRatio(hexA, hexB) {
    var a = luminance(hexA), b = luminance(hexB);
    return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
  }

  /* ---------- renderer ----------
   * state: { tpl, bgHex, name, nameHex, message, msgHex,
   *          photoSrc (canvas|img|null), zoom, ox, oy }   (ox/oy design units)
   * scale: canvas px per design unit (print = 2). */
  function render(canvas, scale, state, fkey) {
    var tpl = state.tpl;
    canvas.width = Math.round(W * scale);
    canvas.height = Math.round(H * scale);
    var ctx = canvas.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.save();
    ctx.scale(scale, scale);

    /* 1 · background */
    ctx.fillStyle = state.bgHex || tpl.bgDefault;
    ctx.fillRect(0, 0, W, H);

    /* 2 · photo (under art + text, like the originals) */
    var hasMsg = !!(state.message && state.message.trim() && tpl.fields.message && tpl.fields.message.zone.cy > H / 2);
    var pz = photoZone(tpl, state, hasMsg);
    if (state.photoSrc) {
      var z = pz;
      var zh = pz.h;
      var pw = state.photoSrc.width || state.photoSrc.naturalWidth;
      var ph = state.photoSrc.height || state.photoSrc.naturalHeight;
      if (pw && ph) {
        /* fit 'whole' = the entire image shows (logos, flyers); default fills the zone */
        var base = state.fit === 'whole' ? Math.min(z.w / pw, zh / ph) : Math.max(z.w / pw, zh / ph);
        var cover = base * (state.zoom || 1);
        var dw = pw * cover, dh = ph * cover;
        var dx = z.x + z.w / 2 - dw / 2 + (state.ox || 0);
        var dy = z.y + zh / 2 - dh / 2 + (state.oy || 0);
        ctx.save();
        ctx.beginPath();
        ctx.rect(z.x, z.y, z.w, zh);
        ctx.clip();
        ctx.drawImage(state.photoSrc, dx, dy, dw, dh);
        ctx.restore();
      }
    }

    /* 3 · template art (logo / script / doodles), full-bleed */
    if (state._artImg) ctx.drawImage(state._artImg, 0, 0, W, H);

    /* 4 · footer strip for this jar's flavor, full-bleed */
    if (state._footImg) ctx.drawImage(state._footImg, 0, 0, W, H);

    /* 5 · name + message text */
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    if (tpl.fields.name && state.name) {
      var nz = tpl.fields.name.zone;
      var ns = fitLine(ctx, state.name, nz.maxW, nz.maxSize);
      ctx.font = fontStack(ns);
      ctx.fillStyle = state.nameHex || '#FFFFFF';
      ctx.textAlign = nz.align || 'center';
      ctx.fillText(state.name, nz.align === 'left' ? nz.x : nz.cx, nz.cy + nz.maxSize * 0.5);
      ctx.textAlign = 'center';
    }
    if (tpl.fields.message && state.message) {
      var mz = tpl.fields.message.zone;
      /* bottom messages live in the band between the photo and the flavor footer,
         so a long message shrinks instead of running into either (2026-10-19 #60684) */
      var low = mz.cy > H / 2;
      var bandTop = 0, bandBot = H;
      if (low) {
        bandBot = tpl.footer === 'none' ? H - 8 : FOOT_TOP;
        var pzBottom = pz.y + pz.h;
        var overlapsX = mz.align === 'left'
          ? (mz.x < pz.x + pz.w && mz.x + mz.maxW > pz.x)
          : (mz.cx - mz.maxW / 2 < pz.x + pz.w && mz.cx + mz.maxW / 2 > pz.x);
        bandTop = (overlapsX && pzBottom < mz.cy + mz.maxSize * 0.5) ? pzBottom + 4 : 0;
      }
      var fit = fitWrapped(ctx, state.message, mz.maxW, mz.maxSize, mz.lines || 2, low ? bandBot - bandTop : 0);
      ctx.font = fontStack(fit.size);
      ctx.fillStyle = state.msgHex || '#FFFFFF';
      ctx.textAlign = mz.align || 'center';
      var mx = mz.align === 'left' ? mz.x : mz.cx;
      var lh = fit.size * LH;
      var y0 = mz.cy + mz.maxSize * 0.5 - (fit.lines.length - 1) * lh / 2;
      if (low) {
        var blkBot = y0 + (fit.lines.length - 1) * lh + INK * fit.size;
        if (blkBot > bandBot) y0 -= blkBot - bandBot;
        var blkTop = y0 - INK * fit.size;
        if (blkTop < bandTop) y0 += bandTop - blkTop;
      }
      for (var i = 0; i < fit.lines.length; i++) {
        ctx.fillText(fit.lines[i], mx, y0 + i * lh);
      }
      ctx.textAlign = 'center';
    }
    ctx.restore();
  }

  /* load everything a state needs, then render */
  function prepare(state, fkey) {
    var tpl = state.tpl;
    return Promise.all([
      loadFont(),
      loadImg(tpl.art).then(function (im) { state._artImg = im; }),
      loadImg(footerAsset(tpl, fkey)).then(function (im) { state._footImg = im; })
    ]);
  }

  /* ---------- PDF writer ----------
   * One page per distinct flavor. Each page is the flattened 1550x1800
   * render. Flate (zlib) when CompressionStream exists, JPEG otherwise. */
  function deflate(bytes) {
    if (typeof CompressionStream === 'undefined') return Promise.resolve(null);
    var cs = new CompressionStream('deflate');
    var stream = new Blob([bytes]).stream().pipeThrough(cs);
    return new Response(stream).arrayBuffer().then(function (b) { return new Uint8Array(b); });
  }
  function canvasRGB(canvas) {
    var ctx = canvas.getContext('2d');
    var id = ctx.getImageData(0, 0, canvas.width, canvas.height).data;
    var out = new Uint8Array(canvas.width * canvas.height * 3);
    for (var i = 0, j = 0; i < id.length; i += 4) {
      out[j++] = id[i]; out[j++] = id[i + 1]; out[j++] = id[i + 2];
    }
    return out;
  }
  function canvasJpeg(canvas) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (b) {
        if (!b) return reject(new Error('jpeg failed'));
        b.arrayBuffer().then(function (ab) { resolve(new Uint8Array(ab)); });
      }, 'image/jpeg', 0.92);
    });
  }

  function buildPdf(pages) {
    /* pages: [{data: Uint8Array, filter: 'FlateDecode'|'DCTDecode', w, h}] */
    var enc = new TextEncoder();
    var chunks = [], offsets = [], pos = 0;
    function push(s) {
      var b = typeof s === 'string' ? enc.encode(s) : s;
      chunks.push(b); pos += b.length;
    }
    function obj(n, body, streamBytes) {
      offsets[n] = pos;
      push(n + ' 0 obj\n' + body + '\n');
      if (streamBytes) { push('stream\n'); push(streamBytes); push('\nendstream\n'); }
      push('endobj\n');
    }
    var n = pages.length;
    push('%PDF-1.4\n');
    var kids = [];
    for (var i = 0; i < n; i++) kids.push((3 + i * 3) + ' 0 R');
    obj(1, '<< /Type /Catalog /Pages 2 0 R >>');
    obj(2, '<< /Type /Pages /Kids [' + kids.join(' ') + '] /Count ' + n + ' >>');
    pages.forEach(function (p, i) {
      var pageN = 3 + i * 3, contN = pageN + 1, imgN = pageN + 2;
      obj(pageN, '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ' + PAGE_W + ' ' + PAGE_H + '] ' +
        '/Resources << /XObject << /Im' + i + ' ' + imgN + ' 0 R >> >> /Contents ' + contN + ' 0 R >>');
      var content = 'q ' + PAGE_W + ' 0 0 ' + PAGE_H + ' 0 0 cm /Im' + i + ' Do Q';
      obj(contN, '<< /Length ' + content.length + ' >>', enc.encode(content));
      obj(imgN, '<< /Type /XObject /Subtype /Image /Width ' + p.w + ' /Height ' + p.h +
        ' /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /' + p.filter +
        ' /Length ' + p.data.length + ' >>', p.data);
    });
    var xrefPos = pos;
    var total = 3 + n * 3;
    var x = 'xref\n0 ' + total + '\n0000000000 65535 f \n';
    for (var k = 1; k < total; k++) {
      x += String(offsets[k]).padStart(10, '0') + ' 00000 n \n';
    }
    x += 'trailer\n<< /Size ' + total + ' /Root 1 0 R >>\nstartxref\n' + xrefPos + '\n%%EOF';
    push(x);
    var totalLen = chunks.reduce(function (s, c) { return s + c.length; }, 0);
    var out = new Uint8Array(totalLen), o = 0;
    chunks.forEach(function (c) { out.set(c, o); o += c.length; });
    return out;
  }

  /* jobs: [{state, fkey}] — one PDF page per job, in order. Same-design packs
     pass one shared state with a job per distinct flavor; per-jar packs pass a
     job per jar, each with its own state. */
  function buildPrintPdfJars(jobs) {
    var canvas = document.createElement('canvas');
    var seq = Promise.resolve([]);
    jobs.forEach(function (job) {
      seq = seq.then(function (pages) {
        return prepare(job.state, job.fkey).then(function () {
          render(canvas, 2, job.state, job.fkey);
          var rgb = canvasRGB(canvas);
          return deflate(rgb).then(function (fl) {
            if (fl) {
              pages.push({ data: fl, filter: 'FlateDecode', w: canvas.width, h: canvas.height });
              return pages;
            }
            return canvasJpeg(canvas).then(function (jp) {
              pages.push({ data: jp, filter: 'DCTDecode', w: canvas.width, h: canvas.height });
              return pages;
            });
          });
        });
      });
    });
    return seq.then(function (pages) {
      var bytes = buildPdf(pages);
      return new File([bytes], 'KK-Custom-Pack-PRINT.pdf', { type: 'application/pdf' });
    });
  }
  /* legacy signature: one shared state, one page per distinct flavor */
  function buildPrintPdf(state, flavorEntries) {
    return buildPrintPdfJars(flavorEntries.map(function (fe) {
      return { state: state, fkey: fe.key };
    }));
  }

  window.PPBLabel = {
    TEMPLATES: TEMPLATES,
    W: W, H: H,
    flavorKey: flavorKey,
    footerAsset: footerAsset,
    photoZone: photoZone,
    loadFont: loadFont,
    loadImg: loadImg,
    prepare: prepare,
    render: render,
    buildPrintPdf: buildPrintPdf,
    buildPrintPdfJars: buildPrintPdfJars,
    contrastRatio: contrastRatio
  };
})();
